import React, { useEffect, useState } from 'react';
import { ArrowRight, Star } from 'lucide-react';

/**
 * StickyBottomCTA
 * Slim sticky bar at the bottom of the viewport that appears once the user has
 * scrolled past the Hero section and stays visible for the rest of the page —
 * except when the Contact section (#cta) is already in view (would be redundant).
 * Clicking the primary button opens the QualifyChat modal via the global event.
 */
const StickyBottomCTA = () => {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const hero = document.querySelector('.hero-section');
    if (!hero) return undefined;

    let heroOut = false;
    let ctaIn = false;
    const update = () => setVisible(heroOut && !ctaIn);

    const ioHero = new IntersectionObserver(([e]) => {
      heroOut = !e.isIntersecting;
      update();
    }, { threshold: 0.05 });
    ioHero.observe(hero);

    // #cta is inside a lazy Suspense-mounted section, so it may not exist yet on mount.
    // Poll until it appears, then attach an IntersectionObserver.
    let ioCta;
    let stopped = false;
    const attachCtaObserver = () => {
      if (stopped) return;
      const cta = document.getElementById('cta');
      if (!cta) {
        setTimeout(attachCtaObserver, 500);
        return;
      }
      ioCta = new IntersectionObserver(([e]) => {
        ctaIn = e.isIntersecting;
        update();
      }, { threshold: 0.15 });
      ioCta.observe(cta);
    };
    attachCtaObserver();

    return () => {
      stopped = true;
      ioHero.disconnect();
      if (ioCta) ioCta.disconnect();
    };
  }, []);

  const openChat = () => {
    window.dispatchEvent(new CustomEvent('open-qualify-chat'));
  };

  if (dismissed) return null;

  return (
    <div
      className={`sticky-cta ${visible ? 'is-visible' : ''}`}
      aria-hidden={!visible}
      data-testid="sticky-bottom-cta"
    >
      <div className="sticky-cta-inner">
        <div className="sticky-cta-trust" aria-hidden="true">
          <div className="sticky-cta-stars">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={12} fill="#00FFD1" color="#00FFD1" strokeWidth={0} />
            ))}
          </div>
          <span className="sticky-cta-trust-text">
            <strong>500+ Agenturen</strong> · Kostenloses Erstgespräch
          </span>
        </div>

        <button
          type="button"
          className="sticky-cta-btn"
          onClick={openChat}
          data-testid="sticky-cta-open-chat"
          aria-label="Jetzt Termin sichern – KI-Chat öffnen"
        >
          <span>Jetzt Termin sichern</span>
          <ArrowRight size={15} strokeWidth={2.6} />
        </button>

        <button
          type="button"
          className="sticky-cta-dismiss"
          onClick={() => setDismissed(true)}
          aria-label="Leiste ausblenden"
          data-testid="sticky-cta-dismiss"
        >
          ×
        </button>
      </div>

      <style>{`
        .sticky-cta {
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 90;
          transform: translateY(120%);
          opacity: 0;
          pointer-events: none;
          transition: transform 0.55s cubic-bezier(.22,1,.36,1), opacity 0.4s ease;
          padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
        }
        .sticky-cta.is-visible {
          transform: translateY(0);
          opacity: 1;
          pointer-events: auto;
        }
        .sticky-cta-inner {
          max-width: 960px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 10px 14px 10px 18px;
          border-radius: 999px;
          background:
            linear-gradient(180deg, rgba(10, 12, 22, 0.92), rgba(6, 8, 16, 0.94)) padding-box,
            linear-gradient(135deg, rgba(0, 255, 209, 0.55) 0%, rgba(0, 212, 255, 0.4) 45%, rgba(168, 85, 247, 0.45) 100%) border-box;
          border: 1.5px solid transparent;
          backdrop-filter: blur(16px) saturate(140%);
          -webkit-backdrop-filter: blur(16px) saturate(140%);
          box-shadow:
            0 12px 40px rgba(0, 0, 0, 0.4),
            0 0 22px rgba(0, 255, 209, 0.25);
        }
        .sticky-cta-trust {
          flex: 1;
          min-width: 0;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .sticky-cta-stars {
          display: inline-flex;
          gap: 2px;
          flex-shrink: 0;
        }
        .sticky-cta-trust-text {
          font-size: 13px;
          color: rgba(255,255,255,0.78);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .sticky-cta-trust-text strong {
          color: #FFFFFF;
          font-weight: 800;
        }
        .sticky-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          border: none;
          border-radius: 999px;
          background: linear-gradient(135deg, #00FFD1 0%, #00D4FF 55%, #7C3AED 130%);
          background-size: 180% 100%;
          background-position: 0% 50%;
          color: #061014;
          font-size: 13px;
          font-weight: 800;
          letter-spacing: 0.04em;
          cursor: pointer;
          box-shadow:
            0 0 0 1px rgba(0, 255, 209, 0.55),
            0 0 20px rgba(0, 255, 209, 0.55),
            0 8px 22px rgba(0, 212, 255, 0.35);
          transition: transform .3s cubic-bezier(.22,1,.36,1), background-position .5s ease, box-shadow .3s ease;
          animation: sticky-cta-pulse 3.2s ease-in-out infinite;
          flex-shrink: 0;
        }
        .sticky-cta-btn:hover {
          transform: translateY(-1px);
          background-position: 100% 50%;
          box-shadow:
            0 0 0 1px rgba(0, 255, 209, 0.85),
            0 0 30px rgba(0, 255, 209, 0.85),
            0 12px 30px rgba(0, 212, 255, 0.55);
        }
        @keyframes sticky-cta-pulse {
          0%, 100% {
            box-shadow:
              0 0 0 1px rgba(0, 255, 209, 0.5),
              0 0 18px rgba(0, 255, 209, 0.5),
              0 8px 22px rgba(0, 212, 255, 0.3);
          }
          50% {
            box-shadow:
              0 0 0 1px rgba(0, 255, 209, 0.75),
              0 0 28px rgba(0, 255, 209, 0.75),
              0 8px 22px rgba(0, 212, 255, 0.45);
          }
        }
        .sticky-cta-dismiss {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          border: 1px solid rgba(255,255,255,0.1);
          background: rgba(255,255,255,0.02);
          color: rgba(255,255,255,0.55);
          font-size: 20px;
          line-height: 1;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: color .25s ease, background .25s ease, border-color .25s ease;
          flex-shrink: 0;
        }
        .sticky-cta-dismiss:hover {
          color: #FFFFFF;
          background: rgba(255,255,255,0.06);
          border-color: rgba(255,255,255,0.18);
        }

        @media (max-width: 640px) {
          .sticky-cta { padding: 8px 12px calc(8px + env(safe-area-inset-bottom, 0px)); }
          .sticky-cta-inner {
            padding: 10px 14px;
            gap: 10px;
            justify-content: center;
          }
          /* Hide trust text & dismiss on mobile, center the CTA button */
          .sticky-cta-trust { display: none; }
          .sticky-cta-dismiss { display: none; }
          .sticky-cta-btn {
            padding: 12px 26px;
            font-size: 13.5px;
            width: auto;
            justify-content: center;
            letter-spacing: 0.06em;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .sticky-cta { transition: opacity .2s ease; }
          .sticky-cta-btn { animation: none; }
        }
      `}</style>
    </div>
  );
};

export default StickyBottomCTA;
